import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, Empty } from '../../components/ui';
import { Kpis, Panel, Curve, BarsH } from '../../components/charts';
import { CLAIM_LABEL } from './Claims';

/* ============================================================
   Dashboard prog.

   Tiga garis, dan yang penting adalah jarak antara dua yang
   terakhir. Kalau ketiganya diturunkan dari tempat yang sama,
   jaraknya tidak berarti apa-apa — karena itu rencana datang dari
   baseline dan dua lainnya dari klaim.
   ============================================================ */

type Summary = {
  claims: { id: string; state: string; periodNo: number; periodStatus: string }[];
  latest: {
    claimId: string;
    state: string;
    periodNo: number;
    totals: {
      reported: number;
      verified: number;
      gap: number;
      lines: number;
      verifiedLines: number;
      unverifiedLines: number;
      blockedLines: number;
      equalFallback: boolean;
      worst: { activityId: string; reportedPct: number; verifiedPct: number; gap: number }[];
    };
  } | null;
  programme: { accepted: number; awaitingReview: number };
  weights: { total: number; set: number };
};

type SCurve = {
  labels: string[];
  planned: number[];
  reported: number[];
  verified: number[];
  baseline: string | null;
};

type WbsRow = { code: string; reported: number; verified: number; gap: number; lines: number };

export function Dashboard({ projectId }: { projectId: string }) {
  const nav = useNavigate();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [curve, setCurve] = useState<SCurve | null>(null);
  const [wbs, setWbs] = useState<WbsRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        api.get<Summary>(`/progress/summary?projectId=${projectId}`),
        api.get<SCurve>(`/progress/s-curve?projectId=${projectId}`),
      ]);
      setSummary(s);
      setCurve(c);
      if (s.latest) {
        const w = await api.get<{ rows: WbsRow[] }>(`/progress/claims/${s.latest.claimId}/wbs`);
        setWbs(w.rows);
      }
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the dashboard.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!summary) return <Loading rows={5} />;

  const t = summary.latest?.totals;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Progress Assurance</h1>
          <p>Progres yang bisa dipertahankan, karena seseorang benar-benar berjalan dan melihatnya.</p>
        </div>
      </div>

      {summary.programme.accepted === 0 ? (
        <Empty
          title="Belum ada programme yang diterima"
          body="Impor dan terima satu versi programme lebih dulu. Semua angka di halaman ini menggantung padanya."
          action={
            <button className="btn btn-primary" onClick={() => nav('/programme')}>
              Buka Programme
            </button>
          }
        />
      ) : (
        <>
          <Kpis
            items={[
              {
                label: 'Terverifikasi',
                value: t ? `${t.verified.toFixed(1)}%` : '—',
                note: summary.latest ? `periode ${summary.latest.periodNo}` : 'belum ada klaim',
              },
              {
                label: 'Dilaporkan kontraktor',
                value: t ? `${t.reported.toFixed(1)}%` : '—',
              },
              {
                label: 'Selisih',
                value: t ? `${t.gap > 0 ? '−' : ''}${Math.abs(t.gap).toFixed(1)} pts` : '—',
                note: t && t.gap > 5 ? 'di atas ambang' : undefined,
                tone: t ? (t.gap > 5 ? 'crit' : t.gap > 0 ? 'warn' : 'good') : undefined,
              },
              {
                label: 'Versi menunggu ditinjau',
                value: String(summary.programme.awaitingReview),
                note: summary.programme.awaitingReview > 0 ? 'baca selisihnya sebelum diterima' : undefined,
                tone: summary.programme.awaitingReview > 0 ? 'warn' : undefined,
              },
            ]}
          />

          {summary.weights.set === 0 && summary.weights.total > 0 && (
            <div className="banner">
              Bobot belum ditetapkan untuk satu pun dari {summary.weights.total} aktivitas, jadi
              angka di atas memakai pembagian rata. Itu titik awal, bukan angka yang bisa
              disertifikasi.
            </div>
          )}

          {curve && curve.labels.length > 0 && (
            <Panel
              title="Rencana, dilaporkan, terverifikasi"
              note={
                curve.baseline
                  ? `Garis tipis dari baseline ${curve.baseline}. Dua garis tebal dari klaim per periode.`
                  : 'Baseline belum ditandai, jadi garis rencana kosong.'
              }
              wide
            >
              <Curve
                suffix="%"
                labels={curve.labels}
                series={[
                  { name: 'Rencana', points: curve.planned, color: 'var(--series-3)', kind: 'planned' },
                  { name: 'Dilaporkan', points: curve.reported, color: 'var(--series-2)' },
                  { name: 'Terverifikasi', points: curve.verified, color: 'var(--series-1)', kind: 'actual' },
                ]}
              />
            </Panel>
          )}

          <div className="grid-2">
            {wbs.length > 0 && (
              <Panel title="Per WBS" note="Dilaporkan di belakang, terverifikasi di depan. Yang terpisah paling jauh lebih dulu.">
                <BarsH
                  suffix="%"
                  againstLabel="Dilaporkan"
                  valueLabel="Terverifikasi"
                  rows={wbs.slice(0, 8).map((r) => ({
                    label: r.code,
                    value: r.verified,
                    against: r.reported,
                  }))}
                />
              </Panel>
            )}

            {t && t.worst.length > 0 && (
              <Panel title="Selisih terbesar" note="Baris yang perlu dijelaskan sebelum periode ini disertifikasi.">
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Dilaporkan</th>
                        <th>Terverifikasi</th>
                        <th>Selisih</th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.worst.slice(0, 8).map((w) => (
                        <tr key={w.activityId} className="no-click">
                          <td>{w.reportedPct.toFixed(1)}%</td>
                          <td>{w.verifiedPct.toFixed(1)}%</td>
                          <td className={w.gap > 5 ? 'neg' : ''}>−{w.gap.toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            )}
          </div>

          {summary.claims.length > 0 && (
            <Panel title="Klaim" note="Semua periode, terbaru lebih dulu." wide>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Periode</th>
                      <th>Status klaim</th>
                      <th>Status periode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.claims.map((c) => (
                      <tr key={c.id} onClick={() => nav(`/claims/${c.id}`)}>
                        <td className="num">P{c.periodNo}</td>
                        <td>{CLAIM_LABEL[c.state] ?? c.state}</td>
                        <td>{c.periodStatus}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}
        </>
      )}
    </>
  );
}
